import {
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  Button,
  Datagrid,
  EditButton,
  FormDataConsumer,
  Identifier,
  List,
  PasswordInput,
  ReferenceInput,
  SimpleForm,
  TextField,
  TextInput,
  useCreatePath,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin';
import {
  IconButton,
  TextField as MuiTextField,
  Checkbox as MuiCheckbox,
  Dialog,
  DialogContent,
  DialogTitle, Typography, DialogActions,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { KioskConfig } from '@x-guard/xgac-types/xgac';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import * as React from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';
import DateFieldNullable from '../../../components/fields/DateFieldNullable';
import { getCurrentCustomer } from '../../../lib/currentCustomer';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import { PositionForm } from '../../../components/baseForms/PositionForm';
import { PhoneNumberInput } from '../../../components/inputs/PhoneNumberInput';
import ReferenceFieldNullable from '../../../components/fields/ReferenceFieldNullable';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { HasRoles } from '../../../components/HasRoles';
import CustomBulkActionButtons from '../../../components/buttons/CustomBulkActionButtons';
import { XGAC_KIOSK_URL } from '../../../config';
import { KioskConfigInput, kioskConfigTransformer } from '../../../components/inputs/KioskConfig';

const Grid = Grid2;

const kioskUserCreateTransformer = (data: any) => {

  if (data.device?.kioskConfig) {

    data.device.kioskConfig = kioskConfigTransformer(data.device.kioskConfig);

  }
  return {
    user: {
      name: data.asset?.name,
      username: data.user?.username,
      properties: {
        phoneNumber: data.asset?.properties?.phoneNumber,
      },
    },
    device: {
      kioskConfig: data.device?.kioskConfig,
    },
    asset: {
      name: data.asset?.name,
      position: { ...data.asset?.position, type: 'Point' },
      properties: {
        phoneNumber: data.asset?.properties?.phoneNumber,
      },
    },
    password: data.password,
  };

};

const kioskUserEditTransformer = (data: any) => {

  if (data.device?.kioskConfig) {

    data.device.kioskConfig = kioskConfigTransformer(data.device.kioskConfig);

  }

  return {
    user: {
      name: data.asset?.name,
      properties: {
        phoneNumber: data.asset?.properties?.phoneNumber,
      },
    },
    device: {
      kioskConfig: data.device?.kioskConfig,
    },
    asset: {
      name: data.asset?.name,
      position: { ...data.asset?.position, type: 'Point' },
      properties: {
        phoneNumber: data.asset?.properties?.phoneNumber,
      },
    },
  };

};
const useGetCustomerKioskConfig = () => {

  const currentCustomer = getCurrentCustomer()?.value;
  const [lastCurrentCustomer, setLastCurrentCustomer] = useState<string | null>(null);
  const [kioskConfig, setKioskConfig] = useState<KioskConfig | null>(null);

  useEffect(() => {

    if (currentCustomer && currentCustomer !== lastCurrentCustomer) {

      setLastCurrentCustomer(currentCustomer);
      xgacDataProvider.getOne('customers', { id: currentCustomer }).then(({ data }) => {

        setKioskConfig(data.kioskConfig);

      });

    }

  }, [currentCustomer, lastCurrentCustomer]);

  return kioskConfig;

};

const KioskDefaultConfigField = (props: { label: string }) => {

  const customerKioskConfig = useGetCustomerKioskConfig();
  const record: KioskConfig & { id: Identifier } = useRecordContext();
  const recordKioskConfig: KioskConfig | undefined = _.get(record, 'device.kioskConfig');

  let value = true;
  if (!customerKioskConfig && !recordKioskConfig) {

    value = true;

  } else {

    const filteredRecordKioskConfig = _.omit(recordKioskConfig, ['zone', 'managed', 'exposeLabels']);
    value = Object.keys(filteredRecordKioskConfig).length === 0;

  }

  return <BooleanField source="value" record={{ value }} {...props}/>;

};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const KioskZoneField = (props: any) => {

  const navigate = useNavigate();
  const createPath = useCreatePath();
  const record = useRecordContext();
  const zoneId = _.get(record, 'kioskConfig.zone._id');
  if (!zoneId) {

    return <Button
      onClick={() => navigate(createPath({ resource: 'specials/kiosk-user', type: 'edit', id: record.id }))}
      label="resources.kiosk-users.text.set_zone"
      color="error"
    />;

  }
  return (
    <ReferenceFieldNullable reference="zones" source='kioskConfig.zone._id'/>
  );

};

export const KioskUserList = () => {

  return <List title="resources.kiosk-users.title" >
    <Datagrid rowClick="toggleSelection" bulkActionButtons={<CustomBulkActionButtons/>}>
      <TextField source="asset.name" label="general.fields.name"/>
      <KioskZoneField label="resources.zones.text.title_single"/>
      <DateFieldNullable source="asset.lastObservationAt" showTime label="resources.users.fields.lastActiveAt"/>
      <BooleanField source="kioskConfig.managed" label="resources.kiosk-users.fields.managed"/>
      <KioskDefaultConfigField label="resources.kiosk-users.default_config"/>
      <HasRoles anyOf={['admin', 'developer_admin']}>
        <EditButton/>
      </HasRoles>
    </Datagrid>
  </List>;

};

const KioskUserSharedFields = () => {

  return (<>
    <TextInput source="asset.name" label="general.fields.name" required/>
    <PositionForm source="asset.position" />
    <ReferenceInput reference="zones" source='device.kioskConfig.zone._id'>
      <AutocompleteInput autoHighlight={true} label="resources.zones.text.title_single" isRequired />
    </ReferenceInput>
    <PhoneNumberInput source="asset.properties.phoneNumber"/>
    <KioskConfigInput source={'device.kioskConfig'} />
  </>);

};

export const KioskUserCreate = () => {

  const translate = useTranslate();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [showOnceChecked, setShowOnceChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [useOnce, setUseOnce] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const createSuccess = (data: any) => {

    if (data.kioskConfig.managed === true) {

      navigate(-1);
      notify('resources.kiosk-users.text.created');
      refresh();

    } else {

      setUrl(`${XGAC_KIOSK_URL}/${data.user.username}/${data.password}`);
      setOpen(true);

    }

  };
  const handleClose = () => {

    setOpen(false);
    navigate(-1);
    refresh();

  };

  const CopyIconButton = () => {

    return <IconButton onClick={() => {

      copy(url || '');
      notify('general.text.copied');

    }
    }><ContentCopyIcon/></IconButton>;

  };

  return (
    <>
      <CreateWithSuccess title="resources.kiosk-users.title" transform={kioskUserCreateTransformer} mutationOptions={{ onSuccess: createSuccess }}>
        <SimpleForm>
          <KioskUserSharedFields />
          <HasRoles anyOf={['admin', 'developer_admin']}>
            <BooleanInput source="device.kioskConfig.managed" label="resources.kiosk-users.fields.kioskConfig.managed" shouldUnregister />
          </HasRoles>
          <FormDataConsumer>
            {({ formData }) => (formData?.device?.kioskConfig?.managed === true)
                && (
                  <>
                    <TextInput source="user.username" label="resources.kiosk-users.fields.fully_android_id" required shouldUnregister/>
                    <PasswordInput source="password" label="resources.kiosk-users.fields.fully_device_id" required shouldUnregister/>
                  </>
                )
            }
          </FormDataConsumer>
        </SimpleForm>
      </CreateWithSuccess>
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle>
          {translate('resources.kiosk-users.url_dialog_title')}
        </DialogTitle>
        <DialogContent>
          <MuiTextField
            label={false}
            disabled
            value={url}
            fullWidth
            InputProps={{ endAdornment: <CopyIconButton/> }}
            hiddenLabel
            sx={{ mb: 2 }}
          />
          <Grid container>
            <Grid sm={1}>
              <MuiCheckbox value={privacyChecked} onChange={(e) => setPrivacyChecked(e.target.checked)}/>
            </Grid>
            <Grid sm={11}>
              <Typography fontSize="12px" display='inline' variant="body1">{translate('resources.kiosk-users.url_dialog_privacy')}</Typography>
            </Grid>
            <Grid sm={1}>
              <MuiCheckbox value={showOnceChecked} onChange={(e) => setShowOnceChecked(e.target.checked)}/>
            </Grid>
            <Grid sm={11}>
              <Typography fontSize="12px" display='inline' variant="body1">{translate('resources.kiosk-users.url_dialog_show_once')}</Typography>
            </Grid>
            <Grid sm={1}>
              <MuiCheckbox value={useOnce} onChange={(e) => setUseOnce(e.target.checked)}/>
            </Grid>
            <Grid sm={11}>
              <Typography fontSize="12px" display='inline' variant="body1">{translate('resources.kiosk-users.url_dialog_use_once')}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={!showOnceChecked || !privacyChecked || !useOnce} color="primary" label="general.text.close"/>
        </DialogActions>
      </Dialog>
    </>
  );

};

export const KioskUserEdit = () => {

  return <EditWithSuccess title="resources.kiosk-users.title" transform={kioskUserEditTransformer}>
    <SimpleForm>
      <KioskUserSharedFields/>
      <HasRoles anyOf={['admin', 'developer_admin']}>
        <BooleanInput source="device.kioskConfig.managed" label="resources.kiosk-users.fields.kioskConfig.managed" shouldUnregister disabled />
      </HasRoles>
    </SimpleForm>
  </EditWithSuccess>;

};
